import { inject, InjectionToken } from '@angular/core';

import { createSelector, StoreConfig } from '@ngrx/store';

import { CurrentUserSelectors } from '@recruitee/account-data';
import { getProductCodenameFromSubscription } from '@recruitee/billing-plans';
import { COMPANY_ID } from '@recruitee/common';
import type { Company } from '@recruitee/company-types';
import { createActionsWithReducer, createFeatureSelector, ngrxEvent } from '@recruitee/ngrx';
import type { PricingFeatureSubscription } from '@recruitee/subscription-types';

import { accountFeatureKey } from '../tokens';

export interface AccountState {
  currentCompanyId: number | null;
}

export const AccountConfigToken = new InjectionToken('AccountConfigToken', {
  providedIn: 'root',
  factory: (): StoreConfig<AccountState> => ({
    initialState: {
      currentCompanyId: inject(COMPANY_ID),
    },
  }),
});

const initialState: AccountState = {
  currentCompanyId: null,
};

const baseSelector = createFeatureSelector<AccountState>(accountFeatureKey);
const { actions, reducer } = createActionsWithReducer('Account', initialState, {
  setCurrentCompany: (state, payload: { id: Company['id'] }) => ({ currentCompanyId: payload.id }),
  changeCompany: ngrxEvent<Company>(),
  deleteRequest: ngrxEvent<string>(),
  redirectToBlockedAccount: ngrxEvent<void>(),
  signOut: (state, payload?: { afterSignOutUrl?: string }) => state,
});
export { actions as AccountActions, reducer as accountReducer };

const currentCompanySelector = createSelector(
  baseSelector,
  CurrentUserSelectors.data,
  function currentCompany(accountState, currentUser) {
    return currentUser?.companies
      ? currentUser.companies.find(({ id }: { id: number }) => id === accountState.currentCompanyId)
      : null;
  },
);

export const isEEOEnabledSelector = createSelector(
  currentCompanySelector,
  company => <boolean>company?.settings.eeoEnabled,
);

export const isSSOEnabledSelector = createSelector(
  currentCompanySelector,
  company => company?.ssoActive,
);

export const areRequisitionsEnabledSelector = createSelector(
  currentCompanySelector,
  company => !!company?.settings.requisitionsEnabled,
);

const productCodenameSelector = createSelector(
  currentCompanySelector,
  company =>
    getProductCodenameFromSubscription(<PricingFeatureSubscription>company?.subscription) || null,
);

export const selectSettings = () =>
  createSelector(currentCompanySelector, company => (company ? company.settings : null));

export const selectAbilities = () =>
  createSelector(currentCompanySelector, company => (company ? company.abilities : []));

const isMultilangEnabled = createSelector(
  currentCompanySelector,
  company => company?.settings.multilangEnabled || false,
);

const currentCompanyName = createSelector(currentCompanySelector, company => company?.name);

export const AccountSelectors = {
  currentCompany: currentCompanySelector,
  productCodename: productCodenameSelector,
  isEEOEnabled: isEEOEnabledSelector,
  isSSOEnabled: isSSOEnabledSelector,
  isMultilangEnabled,
  areRequisitionsEnabled: areRequisitionsEnabledSelector,
  selectIsGdprSettingEnabled: createSelector(
    selectSettings(),
    settings => settings && settings['gdprEnabled'],
  ),
  currentCompanyWithUser: createSelector(
    currentCompanySelector,
    CurrentUserSelectors.data,
    function currentCompanyWithUser(currentCompany, currentUser) {
      return currentUser
        ? {
            currentCompany: currentCompany || null,
            currentUser,
          }
        : null;
    },
  ),
  currentCompanyName,
};
